import { getImportHistory } from "@/services/location-service";
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	ExclamationCircleOutlined,
	FileExcelOutlined,
} from "@ant-design/icons";
import { Modal, Table, Tag, Tooltip, Spin, Empty, Typography } from "antd";
import { useEffect, useState } from "react";

const { Text } = Typography;

interface ImportHistoryModalProps {
	open: boolean;
	onClose: () => void;
}

const ImportHistoryModal = ({ open, onClose }: ImportHistoryModalProps) => {
	const [records, setRecords] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (open) {
			setLoading(true);
			getImportHistory()
				.then(setRecords)
				.finally(() => setLoading(false));
		}
	}, [open]);

	const columns = [
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 90,
			render: (status: string) => {
				if (status === "completed") {
					return <Tag icon={<CheckCircleOutlined />} color="success">Success</Tag>;
				}
				if (status === "partial") {
					return <Tag icon={<ExclamationCircleOutlined />} color="warning">Partial</Tag>;
				}
				return <Tag icon={<CloseCircleOutlined />} color="error">Failed</Tag>;
			},
		},
		{
			title: "File",
			dataIndex: "file_name",
			key: "file_name",
			ellipsis: true,
			render: (name: string) => (
				<span>
					<FileExcelOutlined style={{ color: "#52c41a", marginRight: 6 }} />
					{name}
				</span>
			),
		},
		{
			title: "Imported",
			key: "counts",
			width: 100,
			render: (_: any, record: any) => (
				<span>
					<Text type="success">{record.success_count}</Text>
					{record.failed_count > 0 && (
						<>
							{" / "}
							<Tooltip
								title={
									record.errors?.length > 0
										? record.errors.slice(0, 5).join("\n")
										: "No error details"
								}
							>
								<Text type="danger">{record.failed_count} failed</Text>
							</Tooltip>
						</>
					)}
					<Text type="secondary"> of {record.total_rows}</Text>
				</span>
			),
		},
		{
			title: "By",
			dataIndex: "user_name",
			key: "user_name",
			width: 120,
			ellipsis: true,
		},
		{
			title: "Date",
			dataIndex: "created_timestamp",
			key: "created_timestamp",
			width: 150,
			render: (date: string) => {
				if (!date) return "-";
				const d = new Date(date);
				return d.toLocaleDateString("en-US", {
					month: "short",
					day: "numeric",
					year: "numeric",
					hour: "numeric",
					minute: "2-digit",
				});
			},
		},
	];

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title="Import History"
			width={700}
			footer={null}
		>
			{loading ? (
				<div style={{ textAlign: "center", padding: 40 }}>
					<Spin />
				</div>
			) : records.length === 0 ? (
				<Empty description="No imports yet" />
			) : (
				<Table
					dataSource={records}
					columns={columns}
					rowKey="id"
					pagination={{ pageSize: 10, size: "small" }}
					size="small"
				/>
			)}
		</Modal>
	);
};

export { ImportHistoryModal };
